import type { TokenScope } from '@dynamic-labs/sdk-api-core';

import type { DynamicClient } from '../../../client/types';
import { assertDefined } from '../../../utils/assertDefined';
import { updateAuthFromVerifyResponse } from '../../auth/updateAuthFromVerifyResponse';
import { getWalletAccounts } from '../getWalletAccounts';
import { formatSignatureForHardwareWallet } from '../hardwareWallet/formatSignatureForHardwareWallet';
import { proveWalletAccountOwnership } from '../proveWalletAccountOwnership';
import { removeUnverifiedWalletAccount } from '../unverifiedWalletAccounts/removeUnverifiedWalletAccount';
import { getWalletProviderFromWalletAccount } from '../utils/getWalletProviderFromWalletAccount';
import { isSameAddress } from '../utils/isSameAddress';
import { verifyMessageSignatureOwnership } from '../verifyMessageSignatureOwnership';
import type { WalletAccount } from '../walletAccount';
import { updateWalletProviderKeysForVerifiedCredentials } from '../walletProvider/walletProviderKeyMap/updateWalletProviderKeysForVerifiedCredentials';
import { WalletAccountAlreadyVerifiedError } from './../../../errors/WalletAccountAlreadyVerifiedError';

type VerifyWalletAccountForSignInOrTransferParams = {
  isTransfer: boolean;
  requestedScopes?: TokenScope[];
  walletAccount: WalletAccount;
};

export const verifyWalletAccountForSignInOrTransfer = async (
  {
    walletAccount,
    isTransfer,
    requestedScopes,
  }: VerifyWalletAccountForSignInOrTransferParams,
  client: DynamicClient
): Promise<WalletAccount> => {
  if (walletAccount.verifiedCredentialId) {
    throw new WalletAccountAlreadyVerifiedError(walletAccount.address);
  }

  const walletProvider = getWalletProviderFromWalletAccount(
    {
      walletAccount,
    },
    client
  );

  const { messageToSign, signature } = await proveWalletAccountOwnership(
    {
      walletAccount,
    },
    client
  );

  const formattedSignature = walletAccount.hardwareWalletVendor
    ? formatSignatureForHardwareWallet({
        hardwareWalletVendor: walletAccount.hardwareWalletVendor,
        signature,
      })
    : signature;

  const { networkId } = await walletProvider.getActiveNetworkId();

  const response = await verifyMessageSignatureOwnership(
    {
      addressesWithTypes: walletAccount.addressesWithTypes,
      chain: walletAccount.chain,
      isTransfer,
      messageToSign,
      networkId,
      requestedScopes,
      signature: formattedSignature,
      walletAddress: walletAccount.address,
      walletDisplayName: walletProvider.metadata.displayName,
      walletProviderType: walletProvider.walletProviderType,
    },
    client
  );

  const walletVerifiedCredential = response.user?.verifiedCredentials.find(
    (verifiedCredential) =>
      isSameAddress(
        verifiedCredential.address ?? '',
        walletAccount.address,
        walletAccount.chain
      )
  );

  assertDefined(
    walletVerifiedCredential,
    `After verifying, still unable to find verified wallet credential for wallet account ${walletAccount.address} on chain ${walletAccount.chain}`
  );

  updateWalletProviderKeysForVerifiedCredentials(
    {
      keysToUpdate: {
        [walletVerifiedCredential.id]: walletProvider.key,
      },
    },
    client
  );

  /**
   * Adds the user with the new wallet verified credential
   * This will add the new verified wallet account to the client`s state
   */
  updateAuthFromVerifyResponse({ response }, client);

  /**
   * Remove the now verified wallet account from the unverified wallet accounts
   */
  removeUnverifiedWalletAccount(
    { unverifiedWalletAccount: walletAccount },
    client
  );

  const verifiedWalletAccount = getWalletAccounts(client).find(({ address }) =>
    isSameAddress(address, walletAccount.address, walletAccount.chain)
  );

  assertDefined(
    verifiedWalletAccount,
    `Unable to find wallet account ${walletAccount.address} on chain ${walletAccount.chain} for verified credential ${walletVerifiedCredential.id}`
  );

  return verifiedWalletAccount;
};
